import { Box, Text } from "@radix-ui/themes";
import type { SponsorsReportPoint } from "@/types/sponsors_report";
import { formatCount, formatDay } from "./stats";

interface Props {
  series: SponsorsReportPoint[];
}

// A hand-rolled sparkline: one polyline over the day-by-day impressions the
// rollup already hands us sorted and zero-filled. Deliberately no charting
// dependency — the whole chart is a few dozen lines of SVG.
const WIDTH = 640;
const HEIGHT = 180;
const PAD_X = 12;
const PAD_TOP = 16;
const PAD_BOTTOM = 28;

export default function ImpressionsChart({ series }: Props) {
  if (series.length === 0) return null;

  const max = Math.max(...series.map((p) => p.impressions), 0);
  const innerWidth = WIDTH - PAD_X * 2;
  const innerHeight = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const baseline = PAD_TOP + innerHeight;

  const x = (i: number) =>
    series.length === 1
      ? WIDTH / 2
      : PAD_X + (i / (series.length - 1)) * innerWidth;
  const y = (v: number) =>
    max === 0 ? baseline : baseline - (v / max) * innerHeight;

  const points = series.map((p, i) => ({
    ...p,
    cx: x(i),
    cy: y(p.impressions),
  }));
  const polyline = points
    .map((p) => `${p.cx.toFixed(1)},${p.cy.toFixed(1)}`)
    .join(" ");
  const first = series[0];
  const last = series[series.length - 1];

  return (
    <Box>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Impressions per day from ${formatDay(first.date, true)} to ${formatDay(last.date, true)}`}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          overflow: "visible",
        }}
      >
        {/* Baseline and the max gridline, so the eye has a scale. */}
        <line
          x1={PAD_X}
          x2={WIDTH - PAD_X}
          y1={baseline}
          y2={baseline}
          stroke="var(--gray-a6)"
        />
        {max > 0 && (
          <line
            x1={PAD_X}
            x2={WIDTH - PAD_X}
            y1={PAD_TOP}
            y2={PAD_TOP}
            stroke="var(--gray-a4)"
            strokeDasharray="4 4"
          />
        )}

        {series.length > 1 ? (
          <polyline
            points={polyline}
            fill="none"
            stroke="var(--accent-9)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : (
          <circle
            cx={points[0].cx}
            cy={points[0].cy}
            r={4}
            fill="var(--accent-9)"
          />
        )}

        {/* Invisible hit targets: the browser's native tooltip does the rest. */}
        {points.map((p) => (
          <circle key={p.date} cx={p.cx} cy={p.cy} r={8} fill="transparent">
            <title>{`${formatDay(p.date, true)}: ${formatCount(p.impressions)} impressions, ${formatCount(p.clicks)} clicks`}</title>
          </circle>
        ))}

        <text x={PAD_X} y={PAD_TOP - 4} fontSize={11} fill="var(--gray-11)">
          {max > 0 ? formatCount(max) : ""}
        </text>
        <text x={PAD_X} y={HEIGHT - 8} fontSize={11} fill="var(--gray-11)">
          {formatDay(first.date)}
        </text>
        <text
          x={WIDTH - PAD_X}
          y={HEIGHT - 8}
          fontSize={11}
          fill="var(--gray-11)"
          textAnchor="end"
        >
          {formatDay(last.date)}
        </text>
      </svg>
      {max === 0 && (
        <Text size="2" color="gray">
          No impressions in this range yet.
        </Text>
      )}
    </Box>
  );
}
